<script lang="ts">
  import { fade } from 'svelte/transition';
  import Close from '$icons/CloseAltIcon.svelte';
  import Tag from '$lib/components/common/tagging/Tag.svelte';
  import type {
    FilterConfig,
    FilterConfigDynamic,
    FilterConfigStatic,
    FilterValidityRecord,
    PeopleFiltersSchemaWithDynamic,
    PostFiltersSchema,
    PostFiltersSchemaWithDynamic
  } from '$src/utils/api/feed.util';
  import Plus from '$icons/PlusAltIcon.svelte';
  import EditDefaultLabel from '../../common/inputs/textInputs/editableLabels/EditDefaultLabel.svelte';
  import { onMount } from 'svelte';
  import FilterGroupAccordion from '$lib/components/homepage/accordions/FilterGroupAccordion.svelte';

  type CustomFilter = { label: string; isValid: boolean; value: string; path: string };

  export let title: string;
  export let subtitle = '';
  export let filterConfigs: FilterConfig[];
  export let filterDetails: PostFiltersSchemaWithDynamic | PeopleFiltersSchemaWithDynamic;
  export let validity: FilterValidityRecord;
  export let useAccordion = true;
  export let selectOnMountByIds: string[] = [];
  export let showAddCustom = false;
  export let customFilters: CustomFilter[] = [];
  export let sectionName: string = null;
  export let subcategoryName: string = null;

  if (showAddCustom && (!customFilters || !sectionName || !subcategoryName)) {
    console.error(
      'showAddCustom is set to true which is incopatible with ' +
        JSON.stringify({ customFilters, categoryName: sectionName, subcategoryName })
    );
  }

  export let selectedFilterConfigs: FilterConfig[] = [];

  function onConfigSelect(filter: FilterConfig) {
    selectedFilterConfigs.push(filter);
    selectedFilterConfigs.sort((a, b) => filterConfigs.indexOf(a) - filterConfigs.indexOf(b));
    selectedFilterConfigs = selectedFilterConfigs;
  }

  function onConfigDeselect(filter: FilterConfig) {
    if (filter.dynamicInputConfig) {
      delete filterDetails.dynamicFilters[filter.id];
    } else {
      delete filterDetails[filter.id];
    }

    filterDetails = filterDetails;
    selectedFilterConfigs = selectedFilterConfigs.filter(i => i.id != filter.id);

    delete validity[filter.label];
    validity = validity;
  }

  export function clearAll() {
    selectedFilterConfigs = [];

    for (const k in filterIdToSelected) {
      filterIdToSelected[k] = false;
    }
  }

  let isExpanded = false;
  const filterIdToSelected: Partial<Record<keyof PostFiltersSchema, boolean>> = {};

  for (const i of selectedFilterConfigs) {
    filterIdToSelected[i.id] = true;
  }

  function onSelectedFilterConfigsChange() {
    isExpanded = selectedFilterConfigs.length > 0;
  }

  $: selectedFilterConfigs, onSelectedFilterConfigsChange();

  for (const key of selectOnMountByIds) {
    const conf = filterConfigs.find(i => i.id === key);

    if (conf) {
      selectedFilterConfigs.push(conf);
      filterIdToSelected[key] = true;
    }
  }

  // Custom filters
  function getFilterPath(label: string) {
    return `${subcategoryName}.${sectionName}.${label}`;
  }

  function onAddCustom() {
    const id = customFilters.length;
    const label = 'Custom filter ' + (id + 1);
    const path = getFilterPath(label);

    const filter: CustomFilter = {
      path,
      label,
      isValid: false,
      value: ''
    };

    customFilters.push(filter);
    customFilters = customFilters;

    updateFiltersWithCustom();

    isExpanded = true;
  }

  function onEditCustom(filter: CustomFilter, newLabel: string) {
    pathsToRemove.push(filter.path);

    filter.label = newLabel;
    filter.path = getFilterPath(newLabel);
    customFilters = customFilters;

    updateFiltersWithCustom();
  }

  function onDeleteCustom(filter: CustomFilter) {
    pathsToRemove.push(filter.path);
    customFilters = customFilters.filter(i => i.label !== filter.label);

    updateFiltersWithCustom();
  }

  let pathsToRemove: string[] = [];

  function updateFiltersWithCustom() {
    for (const path of pathsToRemove) {
      delete filterDetails.dynamicFilters[path];
      delete validity[path];
    }

    pathsToRemove = [];

    for (const filter of customFilters) {
      filterDetails.dynamicFilters[filter.path] = filter.value;
      validity[filter.path] = filter.isValid;
    }

    filterDetails = filterDetails;
    validity = validity;
  }

  function loadCustom() {
    const ignoredPaths = filterConfigs.map(i => i.id);

    if (filterDetails?.dynamicFilters) {
      for (const [path, value] of Object.entries(filterDetails.dynamicFilters)) {
        const splitPath = path.split('.');
        const isFromSubcat = splitPath[0] === subcategoryName && splitPath[1] === sectionName;

        if (ignoredPaths.includes(path) || !isFromSubcat) {
          continue;
        }

        const filter: CustomFilter = {
          path: path,
          label: splitPath[2],
          isValid: true,
          value
        };

        customFilters.push(filter);
      }
    }

    customFilters = customFilters;
  }

  const castConfigStatic = (v: FilterConfig) => v as FilterConfigStatic;
  const castConfigDynamic = (v: FilterConfig) => v as FilterConfigDynamic;

  $: activeFilterCount = selectedFilterConfigs.length + customFilters.length;

  onMount(() => {
    loadCustom();
  });
</script>

<FilterGroupAccordion
  this={FilterGroupAccordion}
  class={$$props.class}
  {title}
  {subtitle}
  isExpandable={activeFilterCount > 0}
  hideArrowAndExpand={!useAccordion}
  bind:isExpanded
>
  <div class="flex gap-3 mt-4 flex-wrap" slot="filter-chips">
    {#each filterConfigs as filter}
      <Tag
        rounded
        highContrast
        selectedIconRight={Close}
        label={filter.label}
        iconLeft={filter.icon}
        on:select={() => onConfigSelect(filter)}
        on:deselect={() => onConfigDeselect(filter)}
        bind:selected={filterIdToSelected[filter.id]}
      />
    {/each}

    {#if showAddCustom}
      <Tag
        rounded
        selectedIconRight={Close}
        label="Add custom"
        iconLeft={Plus}
        green
        selectable={false}
        clickable
        on:click={onAddCustom}
      />
    {/if}
  </div>

  <div class="grid grid-cols-2 gap-4" slot="filter-details">
    {#each selectedFilterConfigs as selectedFilter, index}
      <div transition:fade={{ duration: 200 }} class:mt-4={index === 0 || index === 1}>
        {#if selectedFilter.dynamicInputConfig}
          {@const config = castConfigDynamic(selectedFilter)}

          <svelte:component
            this={selectedFilter.configComponent}
            bind:detail={filterDetails['dynamicFilters'][selectedFilter.id]}
            bind:isValid={validity[selectedFilter.label]}
            inputConfig={config.dynamicInputConfig}
          />
        {:else}
          {@const config = castConfigStatic(selectedFilter)}

          <svelte:component
            this={config.configComponent}
            bind:detail={filterDetails[selectedFilter.id]}
            bind:isValid={validity[selectedFilter.label]}
          />
        {/if}
      </div>
    {/each}

    {#each customFilters as customFilter, index}
      <div transition:fade={{ duration: 200 }} class:mt-4={index === 0 || index === 1}>
        <EditDefaultLabel
          label={customFilter.label}
          placeholder="Enter here"
          on:labelChange={e => onEditCustom(customFilter, e.detail.newLabel)}
          bind:value={customFilter.value}
          on:delete={() => onDeleteCustom(customFilter)}
          deleteable
          on:change={e => {
            customFilter.isValid = !!e.detail.value;
            updateFiltersWithCustom();
          }}
        />
      </div>
    {/each}
  </div>
</FilterGroupAccordion>
